import { lazy } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AdminAttention } from '@shared/admin';
import { api } from '@/lib/api';
import { useIsAdmin } from '@/lib/hooks';
import { openAdmin } from '@/lib/overlays';
import { registerAdminSection, registerCommands, registerSlot, type PaletteCommand } from '@/lib/slots';
import type { ClientArea } from '../types';
import { Console } from './Console';

// The super admin console: its shell, and the Team & access, Currency & general, File storage, AI models & costs and Security sections.
// Other areas add their own sections (backups, keys, email) through registerAdminSection.

const TeamSection = lazy(() => import('./Team').then((m) => ({ default: m.TeamSection })));
const CurrencySection = lazy(() => import('./Currency').then((m) => ({ default: m.CurrencySection })));
const FilesSection = lazy(() => import('./Files').then((m) => ({ default: m.FilesSection })));
const AiSection = lazy(() => import('./Ai').then((m) => ({ default: m.AiSection })));
const SecuritySection = lazy(() => import('./Security').then((m) => ({ default: m.SecuritySection })));

/** One request answers every section's dot; only a super admin asks. */
function useAdminAttention(): AdminAttention | undefined {
  const isAdmin = useIsAdmin();
  return useQuery({
    queryKey: ['admin', 'attention'],
    queryFn: () => api.get<AdminAttention>('/admin/attention'),
    enabled: isAdmin,
    staleTime: 60_000,
  }).data;
}

const attention = (key: keyof AdminAttention) => () => !!useAdminAttention()?.[key];

/** Waits for an element the console is about to render, then focuses it. */
function focusSoon(id: string, tries = 40): void {
  const el = document.getElementById(id);
  if (el) {
    el.focus();
    return;
  }
  if (tries > 0) requestAnimationFrame(() => focusSoon(id, tries - 1));
}

function useAdminCommands(): PaletteCommand[] {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return [];
  return [
    {
      id: 'admin-invite',
      title: 'Invite a teammate',
      sub: 'Admin console · Team & access',
      icon: 'user-plus',
      keywords: 'team member invite add user access link',
      order: 145,
      run: () => {
        openAdmin('team');
        focusSoon('admInviteName');
      },
    },
    {
      id: 'admin-ai-costs',
      title: 'AI models and costs',
      sub: 'Admin console',
      icon: 'cpu',
      keywords: 'openai anthropic claude gpt spend price model key',
      order: 146,
      run: () => openAdmin('ai'),
    },
    {
      id: 'admin-files',
      title: 'File storage',
      sub: 'Admin console · S3 bucket',
      icon: 'drive',
      keywords: 's3 aws bucket upload files storage',
      order: 147,
      run: () => openAdmin('files'),
    },
  ];
}

export const area: ClientArea = {
  name: 'admin',
  setup() {
    registerSlot('admin.console', Console);
    registerAdminSection({ id: 'team', label: 'Team & access', icon: 'users', group: 'Workspace', order: 10, component: TeamSection, useAttention: attention('team') });
    registerAdminSection({ id: 'currency', label: 'Currency & general', icon: 'euro', group: 'Workspace', order: 30, component: CurrencySection, useAttention: attention('currency') });
    registerAdminSection({ id: 'files', label: 'File storage', icon: 'drive', group: 'Data', order: 30, component: FilesSection, useAttention: attention('files') });
    registerAdminSection({ id: 'ai', label: 'AI models & costs', icon: 'cpu', group: 'AI & safety', order: 10, component: AiSection, useAttention: attention('ai') });
    registerAdminSection({ id: 'security', label: 'Security', icon: 'lock', group: 'AI & safety', order: 20, component: SecuritySection, useAttention: attention('security') });
    registerCommands(useAdminCommands);
  },
};
